import { Injectable, computed, inject, signal } from '@angular/core';
import { RoleDto, RolesApiService } from '../users/roles-api.service';
import { SiteTerminologyService } from '../../core/services/site-terminology.service';
import { PaymentsDataService } from '../payments/payments-data.service';

export type TicketPriority = 'Low' | 'Medium' | 'High' | 'Urgent';
export type QuestionType = 'Text' | 'Single Select' | 'Multi Select' | 'Date';
export type StatusColor = 'blue' | 'amber' | 'purple' | 'green' | 'gray' | 'red';
export type ChannelIcon = 'email' | 'phone' | 'portal' | 'chat' | 'walkin' | 'other';
export type TaskStatus = 'Open' | 'In Progress' | 'Completed';

export interface HelpDeskContact {
  id: string;
  name: string;
  company: string;
  email: string;
  phone: string;
  /** Drives the "communicate with person raised" workflow — only customers get notified. */
  isCustomer: boolean;
}

/** A configurable ticket status. Open/Closed/Archived are reserved for workflow and can't be renamed or removed. */
export interface TicketStatusDef {
  id: string;
  name: string;
  color: StatusColor;
  locked: boolean;
}

export interface TicketChannelDef {
  id: string;
  name: string;
  icon: ChannelIcon;
}

export interface CategoryQuestion {
  id: string;
  text: string;
  type: QuestionType;
  options: string[];
  isDefault: boolean;
}

export interface TicketCategory {
  id: string;
  name: string;
  description: string;
  active: boolean;
  /** Role ids from the app's real Roles module (RolesApiService), not names. */
  notifyGroups: string[];
  notifyOnCreate: boolean;
  notifyOnStatusChange: boolean;
  notifyOnPriorityChange: boolean;
  notifyOnComment: boolean;
  notifyOnAssign: boolean;
  questions: CategoryQuestion[];
}

export interface TicketActivity {
  id: string;
  type: 'created' | 'status' | 'comment' | 'assigned' | 'file' | 'task' | 'field' | 'comm';
  who: string;
  when: string;
  text: string;
}

export interface TicketFile {
  id: string;
  name: string;
  url: string;
  uploadedBy: string;
  uploadedAt: string;
}

export interface CommentVersion {
  text: string;
  editedBy: string;
  editedAt: string;
}

export interface TicketComment {
  id: string;
  /** Empty text + files = a pure file upload ("Files" tab entries are comments like this). */
  text: string;
  author: string;
  createdAt: string;
  edited: boolean;
  versions: CommentVersion[];
  files: TicketFile[];
  communicate: boolean;
}

export interface TicketTask {
  id: string;
  title: string;
  status: TaskStatus;
  dueDate: string;
  assignee: string;
}

export interface Ticket {
  id: string;
  title: string;
  status: string;
  priority: TicketPriority;
  category: string;
  description: string;
  occurredAt: string;
  sites: string[];
  images: string[];
  latitude: number;
  longitude: number;
  channel: string;
  personRaisedId: string;
  companyRaised: string;
  dueDate: string;
  communicateWithPerson: boolean;
  tags: string[];
  product: string;
  answers: Record<string, string>;
  createdDate: string;
  createdBy: string;
  assignedTo: string;
  activity: TicketActivity[];
  comments: TicketComment[];
  tasks: TicketTask[];
  /** Values for admin-added custom properties (Settings → Ticket Properties → + Add Property), keyed by property key. */
  customFields?: Record<string, string>;
}

export interface TicketPropertyConfig {
  key: string;
  name: string;
  enabled: boolean;
  required: boolean;
  locked: boolean;
  /** Admin-added property, as opposed to one of the built-in fields — only these can be deleted. */
  custom?: boolean;
}

export interface HelpDeskTerminology {
  navLabel: string;
  singular: string;
  plural: string;
  /** e.g. "TCK" → TCK-1042. Renaming only affects new tickets going forward. */
  ticketPrefix: string;
}

function defaultQuestions(): CategoryQuestion[] {
  return [
    { id: 'q-root-cause', text: 'What is the root cause?', type: 'Text', options: [], isDefault: true },
    { id: 'q-impact', text: 'What is the impact?', type: 'Single Select', options: ['Low — cosmetic only', 'Medium — limited disruption', 'High — operations affected'], isDefault: true },
    { id: 'q-actions', text: 'What are the recommended actions, if any?', type: 'Text', options: [], isDefault: true },
  ];
}

const CURRENT_USER = 'Mani Varma';

@Injectable({ providedIn: 'root' })
export class HelpDeskDataService {
  readonly priorities: TicketPriority[] = ['Low', 'Medium', 'High', 'Urgent'];
  readonly questionTypes: QuestionType[] = ['Text', 'Single Select', 'Multi Select', 'Date'];
  readonly statusColors: StatusColor[] = ['blue', 'amber', 'purple', 'green', 'gray', 'red'];
  readonly sites = ['Denver HQ', 'Aurora DC', 'Boulder Research', 'Colorado Springs', 'Fort Collins Depot', 'Lakewood'];
  readonly agents = ['Sarah Lee', 'Mani Varma'];

  private readonly payments = inject(PaymentsDataService);
  /** Sourced from the Payments module's product/service catalog — active items only. */
  readonly products = computed(() => this.payments.products().filter(p => p.active).map(p => p.name));

  private readonly siteTerm = inject(SiteTerminologyService);
  /** Sourced from Settings → Organization, shared across every module — not editable here. */
  readonly siteLabel = computed(() => this.siteTerm.siteLabel());

  readonly terminology = signal<HelpDeskTerminology>({ navLabel: 'Help Desk', singular: 'Ticket', plural: 'Tickets', ticketPrefix: 'TCK' });

  // Open/Closed/Archived are reserved — workflow (Overdue, quick filters, etc.) keys off these
  // exact names, so they're locked from renaming or deletion. Also seeded with the statuses the
  // demo tickets below already use (On Hold), left unlocked/deletable like any admin-added one.
  readonly statuses = signal<TicketStatusDef[]>([
    { id: 'st-open', name: 'Open', color: 'blue', locked: true },
    { id: 'st-unassigned', name: 'Unassigned', color: 'gray', locked: false },
    { id: 'st-assigned', name: 'Assigned', color: 'blue', locked: false },
    { id: 'st-inprogress', name: 'In Progress', color: 'amber', locked: false },
    { id: 'st-onhold', name: 'On Hold', color: 'purple', locked: false },
    { id: 'st-resolved', name: 'Resolved', color: 'green', locked: false },
    { id: 'st-closed', name: 'Closed', color: 'gray', locked: true },
    { id: 'st-archived', name: 'Archived', color: 'gray', locked: true },
  ]);

  readonly channels = signal<TicketChannelDef[]>([
    { id: 'ch-email', name: 'Email', icon: 'email' },
    { id: 'ch-phone', name: 'Phone', icon: 'phone' },
    { id: 'ch-portal', name: 'Portal', icon: 'portal' },
    { id: 'ch-chat', name: 'Chat', icon: 'chat' },
    { id: 'ch-walkin', name: 'Walk-in', icon: 'walkin' },
  ]);

  // "Notify groups" are the app's real Roles (Settings → Roles), not a Help Desk-specific concept —
  // membership is whoever already has that role, managed on the Users screens.
  private readonly rolesApi = inject(RolesApiService);
  readonly roles = signal<RoleDto[]>([]);
  readonly rolesLoaded = signal(false);

  constructor() {
    // Silent on 403: viewing roles needs `users.roles`, which most roles (Managers included) lack.
    // Notify groups are optional, so the screen simply shows none rather than an error on open.
    this.rolesApi.listRoles({ silentForbidden: true })
      .then(roles => this.roles.set(roles))
      .catch(() => {})
      .finally(() => this.rolesLoaded.set(true));
  }

  readonly contacts = signal<HelpDeskContact[]>([
    { id: 'c1', name: 'Renee Marsh', company: 'Summit Properties', email: 'renee.marsh@summitprops.com', phone: '720 555 0132', isCustomer: true },
    { id: 'c2', name: 'Aaron Reyes', company: 'Apex Retail Group', email: 'aaron@apexretail.com', phone: '303 555 0119', isCustomer: true },
    { id: 'c3', name: 'Diane Mott', company: 'TechBuild Corp', email: 'diane.mott@techbuild.com', phone: '303 555 0164', isCustomer: true },
    { id: 'c4', name: 'Pawan Kalyan', company: 'Mountain West REIT', email: 'pawan.k@mountainwestreit.com', phone: '', isCustomer: true },
    { id: 'c5', name: 'John Smith', company: 'ABC Inc', email: 'john@email.com', phone: '', isCustomer: true },
  ]);

  readonly categories = signal<TicketCategory[]>([
    { id: 'cat1', name: 'Maintenance', description: 'HVAC, plumbing, electrical and general upkeep requests.', active: true, notifyGroups: [], notifyOnCreate: true, notifyOnStatusChange: true, notifyOnPriorityChange: false, notifyOnComment: true, notifyOnAssign: false, questions: defaultQuestions() },
    { id: 'cat2', name: 'Property Damage', description: 'Broken fixtures, structural issues, vandalism or accidents.', active: true, notifyGroups: [], notifyOnCreate: true, notifyOnStatusChange: true, notifyOnPriorityChange: true, notifyOnComment: true, notifyOnAssign: true, questions: [...defaultQuestions(), { id: 'q-vendor', text: 'Was a vendor dispatched?', type: 'Single Select', options: ['Yes', 'No'], isDefault: false }] },
    { id: 'cat3', name: 'General Inquiry', description: 'Questions that don’t need a truck roll or vendor.', active: true, notifyGroups: [], notifyOnCreate: true, notifyOnStatusChange: false, notifyOnPriorityChange: false, notifyOnComment: false, notifyOnAssign: false, questions: defaultQuestions() },
    { id: 'cat4', name: 'Billing', description: 'Invoice questions, payment issues, service plan changes.', active: true, notifyGroups: [], notifyOnCreate: true, notifyOnStatusChange: true, notifyOnPriorityChange: false, notifyOnComment: true, notifyOnAssign: false, questions: defaultQuestions() },
    { id: 'cat5', name: 'Access Request', description: 'Badge, key or system access for staff and vendors.', active: false, notifyGroups: [], notifyOnCreate: true, notifyOnStatusChange: false, notifyOnPriorityChange: false, notifyOnComment: false, notifyOnAssign: false, questions: defaultQuestions() },
  ]);

  readonly propertyConfig = signal<TicketPropertyConfig[]>([
    { key: 'status', name: 'Ticket Status', enabled: true, required: false, locked: false },
    { key: 'priority', name: 'Ticket Priority', enabled: true, required: false, locked: false },
    { key: 'category', name: 'Category', enabled: true, required: true, locked: false },
    { key: 'description', name: 'Description', enabled: true, required: false, locked: false },
    { key: 'occurred', name: 'Date & Time Occurred', enabled: true, required: true, locked: false },
    { key: 'sites', name: 'Site(s)', enabled: true, required: false, locked: false },
    { key: 'images', name: 'Images', enabled: true, required: false, locked: false },
    { key: 'location', name: 'Location', enabled: true, required: false, locked: false },
    { key: 'channel', name: 'Ticket Channel', enabled: true, required: false, locked: false },
    { key: 'personRaised', name: 'Person Raised', enabled: true, required: true, locked: false },
    { key: 'companyRaised', name: 'Company Raised', enabled: true, required: false, locked: false },
    { key: 'due', name: 'Due Date', enabled: true, required: false, locked: false },
    { key: 'communicate', name: 'Communicate with Person Raised', enabled: true, required: false, locked: false },
    { key: 'tags', name: 'Tags', enabled: true, required: false, locked: false },
    { key: 'product', name: 'Product or Service', enabled: false, required: false, locked: false },
  ]);

  readonly tickets = signal<Ticket[]>([
    { id: 'TCK-1049', title: 'HVAC not cooling on 3rd floor', status: 'Open', priority: 'High', category: 'Maintenance', description: 'Tenants on the 3rd floor report the HVAC has not been cooling since Monday morning. Thermostat reads 78°F.', occurredAt: '2026-07-30T08:00', sites: ['Boulder Research'], images: [], latitude: 39.7392, longitude: -104.9903, channel: 'Portal', personRaisedId: 'c1', companyRaised: 'Summit Properties', dueDate: '2026-08-05', communicateWithPerson: true, tags: ['hvac'], product: '', answers: { 'q-root-cause': 'Likely a failed compressor relay.', 'q-impact': 'Medium — limited disruption', 'q-actions': 'Dispatch HVAC vendor to inspect compressor.' }, createdDate: '2026-07-30', createdBy: 'Sarah Lee', assignedTo: 'Sarah Lee', activity: [{ id: 'a1', type: 'created', who: 'Sarah Lee', when: 'Jul 30, 8:10 AM', text: 'Ticket created from portal.' }], comments: [], tasks: [] },
    { id: 'TCK-1048', title: 'Broken window in lobby', status: 'In Progress', priority: 'Urgent', category: 'Property Damage', description: 'Lobby window cracked overnight, possibly from a delivery cart. Glass is intact but needs urgent board-up.', occurredAt: '2026-07-29T00:01', sites: ['Lakewood'], images: [], latitude: 39.70, longitude: -105.08, channel: 'Email', personRaisedId: 'c2', companyRaised: 'Apex Retail Group', dueDate: '2026-08-02', communicateWithPerson: true, tags: ['urgent-review'], product: '', answers: { 'q-root-cause': 'Unknown — under review with security footage.', 'q-impact': 'High — operations affected', 'q-actions': 'Board up immediately, schedule replacement.', 'q-vendor': 'Yes' }, createdDate: '2026-07-29', createdBy: 'Mani Varma', assignedTo: 'Mani Varma', activity: [
      { id: 'a1', type: 'created', who: 'Mani Varma', when: 'Jul 29, 9:14 AM', text: 'Ticket created via email.' },
      { id: 'a2', type: 'status', who: 'Sarah Lee', when: 'Jul 29, 9:40 AM', text: 'Changed status from Open to In Progress.' },
    ], comments: [], tasks: [
      { id: 't1', title: 'Board up broken window', status: 'Completed', dueDate: '2026-07-29', assignee: 'Sarah Lee' },
      { id: 't2', title: 'Get quote for replacement glass', status: 'Open', dueDate: '2026-08-02', assignee: 'Mani Varma' },
    ] },
    { id: 'TCK-1047', title: 'Billing question about invoice INV-2026-050', status: 'Resolved', priority: 'Low', category: 'Billing', description: 'Tenant asking why the emergency callout line item on INV-2026-050 is higher than expected.', occurredAt: '2026-07-27T09:00', sites: ['Aurora DC'], images: [], latitude: 39.73, longitude: -104.83, channel: 'Chat', personRaisedId: 'c3', companyRaised: 'TechBuild Corp', dueDate: '', communicateWithPerson: true, tags: [], product: 'Emergency Callout', answers: { 'q-root-cause': 'After-hours emergency rate applied correctly per contract.', 'q-impact': 'Low — cosmetic only', 'q-actions': 'Sent rate sheet, no adjustment needed.' }, createdDate: '2026-07-27', createdBy: 'Sarah Lee', assignedTo: 'Sarah Lee', activity: [{ id: 'a1', type: 'created', who: 'Sarah Lee', when: 'Jul 27, 1:00 PM', text: 'Ticket created via chat.' }], comments: [], tasks: [] },
    { id: 'TCK-1046', title: 'Request badge access for new hire', status: 'Open', priority: 'Medium', category: 'Access Request', description: 'New warehouse associate starting Monday needs badge access to loading dock and depot floor.', occurredAt: '2026-07-26T10:00', sites: ['Fort Collins Depot'], images: [], latitude: 40.59, longitude: -105.08, channel: 'Portal', personRaisedId: 'c4', companyRaised: 'Mountain West REIT', dueDate: '2026-08-04', communicateWithPerson: true, tags: [], product: '', answers: {}, createdDate: '2026-07-26', createdBy: 'Mani Varma', assignedTo: '', activity: [{ id: 'a1', type: 'created', who: 'Mani Varma', when: 'Jul 26, 10:05 AM', text: 'Ticket created from portal.' }], comments: [], tasks: [] },
    { id: 'TCK-1045', title: 'Elevator making grinding noise', status: 'On Hold', priority: 'High', category: 'Maintenance', description: 'Elevator #2 makes a grinding noise between floors 2 and 3. Taken out of service pending inspection.', occurredAt: '2026-07-25T07:30', sites: ['Denver HQ'], images: [], latitude: 39.74, longitude: -104.99, channel: 'Phone', personRaisedId: 'c5', companyRaised: 'ABC Inc', dueDate: '2026-08-03', communicateWithPerson: false, tags: [], product: '', answers: { 'q-root-cause': 'Suspected worn drive sheave.', 'q-impact': 'Medium — limited disruption', 'q-actions': 'Awaiting parts from elevator contractor.' }, createdDate: '2026-07-25', createdBy: 'Sarah Lee', assignedTo: 'Sarah Lee', activity: [{ id: 'a1', type: 'created', who: 'Sarah Lee', when: 'Jul 25, 7:40 AM', text: 'Ticket created via phone call.' }], comments: [], tasks: [] },
    { id: 'TCK-1044', title: 'General question about service hours', status: 'Closed', priority: 'Low', category: 'General Inquiry', description: 'Tenant asked whether the front desk is staffed on weekends.', occurredAt: '2026-07-22T11:00', sites: ['Boulder Research'], images: [], latitude: 39.99, longitude: -105.27, channel: 'Email', personRaisedId: 'c1', companyRaised: 'Summit Properties', dueDate: '', communicateWithPerson: true, tags: [], product: '', answers: { 'q-root-cause': 'N/A', 'q-impact': 'Low — cosmetic only', 'q-actions': 'Replied with weekend hours.' }, createdDate: '2026-07-22', createdBy: 'Mani Varma', assignedTo: 'Mani Varma', activity: [{ id: 'a1', type: 'created', who: 'Mani Varma', when: 'Jul 22, 11:05 AM', text: 'Ticket created via email.' }], comments: [], tasks: [] },
    { id: 'TCK-1043', title: 'Water leak in parking garage', status: 'Open', priority: 'Urgent', category: 'Property Damage', description: 'Active water leak from ceiling on P2 near the elevator lobby, pooling on the ground.', occurredAt: '2026-07-30T06:15', sites: ['Lakewood'], images: [], latitude: 39.70, longitude: -105.08, channel: 'Walk-in', personRaisedId: 'c2', companyRaised: 'Apex Retail Group', dueDate: '2026-07-31', communicateWithPerson: true, tags: ['urgent-review'], product: '', answers: { 'q-root-cause': 'Suspected sprinkler pipe joint failure.', 'q-impact': 'High — operations affected', 'q-actions': 'Plumber dispatched, area cordoned off.', 'q-vendor': 'Yes' }, createdDate: '2026-07-30', createdBy: 'Sarah Lee', assignedTo: 'Sarah Lee', activity: [{ id: 'a1', type: 'created', who: 'Sarah Lee', when: 'Jul 30, 6:20 AM', text: 'Walk-in report at front desk.' }], comments: [], tasks: [] },
    { id: 'TCK-1042', title: 'Monitoring alert — Aurora DC offline', status: 'In Progress', priority: 'High', category: 'Maintenance', description: 'Automated monitoring flagged the Aurora DC site as offline at 2:14 AM. On-site generator may have failed to kick in during a brief outage.', occurredAt: '2026-07-29T02:14', sites: ['Aurora DC'], images: [], latitude: 39.73, longitude: -104.83, channel: 'Portal', personRaisedId: 'c3', companyRaised: 'TechBuild Corp', dueDate: '2026-08-01', communicateWithPerson: true, tags: ['hvac', 'urgent-review'], product: 'Monitoring Subscription', answers: { 'q-root-cause': 'Generator transfer switch did not engage automatically.', 'q-impact': 'High — operations affected', 'q-actions': 'Technician on site testing transfer switch; monitoring restored via cellular backup in the meantime.' }, createdDate: '2026-07-29', createdBy: 'Mani Varma', assignedTo: 'Sarah Lee', activity: [
      { id: 'a1', type: 'created', who: 'Mani Varma', when: 'Jul 29, 9:14 AM', text: 'Ticket created from portal after automated monitoring alert.' },
      { id: 'a2', type: 'status', who: 'Sarah Lee', when: 'Jul 29, 9:40 AM', text: 'Changed status from Open to In Progress and assigned to on-site technician.' },
      { id: 'a3', type: 'comment', who: 'Sarah Lee', when: 'Jul 29, 11:02 AM', text: 'Commented: On-site tech confirms generator transfer switch fault. Ordering replacement relay, ETA tomorrow.' },
      { id: 'a4', type: 'comment', who: 'Mani Varma', when: 'Jul 30, 8:15 AM', text: 'Commented: Cellular backup holding steady overnight — no further downtime reported.' },
    ], comments: [
      {
        id: 'cm2', text: 'Cellular backup holding steady overnight — no further downtime reported.', author: 'Mani Varma',
        createdAt: 'Jul 30, 8:15 AM', edited: false, versions: [], files: [], communicate: true,
      },
      {
        id: 'cm1', text: 'On-site tech confirms generator transfer switch fault. Ordering replacement relay, ETA tomorrow.', author: 'Sarah Lee',
        createdAt: 'Jul 29, 11:02 AM', edited: true,
        versions: [{ text: 'On-site tech is investigating the generator fault.', editedBy: 'Sarah Lee', editedAt: 'Jul 29, 10:20 AM' }],
        files: [{ id: 'f1', name: 'transfer-switch-panel.jpg', url: '', uploadedBy: 'Sarah Lee', uploadedAt: 'Jul 29, 11:02 AM' }],
        communicate: true,
      },
    ], tasks: [
      { id: 't3', title: 'Test transfer switch after relay replacement', status: 'In Progress', dueDate: '2026-07-31', assignee: 'Sarah Lee' },
      { id: 't4', title: 'Confirm cellular backup billing with carrier', status: 'Open', dueDate: '2026-08-03', assignee: 'Mani Varma' },
    ] },
    { id: 'TCK-1041', title: 'Refill request for filtered water', status: 'Resolved', priority: 'Low', category: 'General Inquiry', description: 'Break room filtered water dispenser needs a refill/cartridge swap.', occurredAt: '2026-07-20T09:00', sites: ['Fort Collins Depot'], images: [], latitude: 40.59, longitude: -105.08, channel: 'Portal', personRaisedId: 'c4', companyRaised: 'Mountain West REIT', dueDate: '', communicateWithPerson: true, tags: [], product: '', answers: { 'q-root-cause': 'Cartridge past service date.', 'q-impact': 'Low — cosmetic only', 'q-actions': 'Swapped cartridge, added to monthly PM.' }, createdDate: '2026-07-20', createdBy: 'Sarah Lee', assignedTo: 'Sarah Lee', activity: [{ id: 'a1', type: 'created', who: 'Sarah Lee', when: 'Jul 20, 9:05 AM', text: 'Ticket created from portal.' }], comments: [], tasks: [] },
  ]);

  private readonly today = '2026-08-03';

  contact(id: string): HelpDeskContact | undefined {
    return this.contacts().find(c => c.id === id);
  }

  roleName(id: string): string {
    return this.roles().find(r => r.id === id)?.name ?? id;
  }

  category(name: string): TicketCategory | undefined {
    return this.categories().find(c => c.name === name);
  }

  getTicket(id: string): Ticket | undefined {
    return this.tickets().find(t => t.id === id);
  }

  /** Open/Closed/Archived are workflow-reserved; a status counts as "terminal" only if it's Closed or Archived. */
  isTerminalStatus(name: string): boolean {
    return name === 'Closed' || name === 'Archived';
  }

  isOverdue(t: Ticket): boolean {
    return !!t.dueDate && t.dueDate < this.today && !this.isTerminalStatus(t.status);
  }

  statusDef(name: string): TicketStatusDef | undefined {
    return this.statuses().find(s => s.name === name);
  }

  statusColorClass(name: string): string {
    return 'st-' + (this.statusDef(name)?.color ?? 'gray');
  }

  channelDef(name: string): TicketChannelDef | undefined {
    return this.channels().find(c => c.name === name);
  }

  channelIcon(name: string): ChannelIcon {
    return this.channelDef(name)?.icon ?? 'other';
  }

  nextTicketId(): string {
    const prefix = this.terminology().ticketPrefix || 'TCK';
    const nums = this.tickets()
      .filter(t => t.id.startsWith(prefix + '-'))
      .map(t => parseInt(t.id.slice(prefix.length + 1), 10))
      .filter(n => !isNaN(n));
    return prefix + '-' + (Math.max(...nums, 1000) + 1);
  }

  addTicket(ticket: Ticket) {
    this.tickets.update(ts => [ticket, ...ts]);
  }

  updateTicket(id: string, changes: Partial<Ticket>) {
    this.tickets.update(ts => ts.map(t => t.id === id ? { ...t, ...changes } : t));
  }

  deleteTicket(id: string) {
    this.tickets.update(ts => ts.filter(t => t.id !== id));
  }

  addActivity(id: string, activity: TicketActivity) {
    this.tickets.update(ts => ts.map(t => t.id === id ? { ...t, activity: [...t.activity, activity] } : t));
  }

  addContact(contact: HelpDeskContact) {
    this.contacts.update(cs => [...cs, contact]);
  }

  nextCategoryId(): string {
    return 'cat' + (this.categories().length + 1) + '-' + Date.now();
  }

  saveCategory(category: TicketCategory) {
    this.categories.update(cs => {
      const idx = cs.findIndex(c => c.id === category.id);
      if (idx === -1) return [...cs, category];
      const copy = [...cs];
      copy[idx] = category;
      return copy;
    });
  }

  // ── Statuses ──────────────────────────────────────────────────────────────
  addStatus(name: string, color: StatusColor) {
    this.statuses.update(ss => [...ss, { id: 'st-' + Date.now(), name, color, locked: false }]);
  }
  renameStatus(id: string, name: string) {
    this.statuses.update(ss => ss.map(s => s.id === id && !s.locked ? { ...s, name } : s));
  }
  recolorStatus(id: string, color: StatusColor) {
    this.statuses.update(ss => ss.map(s => s.id === id ? { ...s, color } : s));
  }
  deleteStatus(id: string) {
    this.statuses.update(ss => ss.filter(s => s.id !== id || s.locked));
  }

  // ── Channels ──────────────────────────────────────────────────────────────
  addChannel(name: string, icon: ChannelIcon) {
    this.channels.update(cs => [...cs, { id: 'ch-' + Date.now(), name, icon }]);
  }
  renameChannel(id: string, name: string) {
    this.channels.update(cs => cs.map(c => c.id === id ? { ...c, name } : c));
  }
  deleteChannel(id: string) {
    this.channels.update(cs => cs.filter(c => c.id !== id));
  }

  // ── Comments / Files ──────────────────────────────────────────────────────
  addComment(ticketId: string, text: string, files: TicketFile[], communicate: boolean) {
    const comment: TicketComment = {
      id: 'cm' + Date.now(), text, author: CURRENT_USER, createdAt: 'Just now',
      edited: false, versions: [], files, communicate,
    };
    this.tickets.update(ts => ts.map(t => t.id === ticketId ? { ...t, comments: [comment, ...t.comments] } : t));
    return comment;
  }

  editComment(ticketId: string, commentId: string, newText: string) {
    this.tickets.update(ts => ts.map(t => {
      if (t.id !== ticketId) return t;
      return {
        ...t,
        comments: t.comments.map(c => {
          if (c.id !== commentId) return c;
          const version: CommentVersion = { text: c.text, editedBy: CURRENT_USER, editedAt: 'Just now' };
          return { ...c, text: newText, edited: true, versions: [...c.versions, version] };
        }),
      };
    }));
  }

  // ── Tasks ─────────────────────────────────────────────────────────────────
  addTask(ticketId: string, title: string, dueDate: string, assignee: string) {
    const task: TicketTask = { id: 'tk' + Date.now(), title, status: 'Open', dueDate, assignee };
    this.tickets.update(ts => ts.map(t => t.id === ticketId ? { ...t, tasks: [...t.tasks, task] } : t));
    return task;
  }

  updateTaskStatus(ticketId: string, taskId: string, status: TaskStatus) {
    this.tickets.update(ts => ts.map(t => t.id === ticketId ? { ...t, tasks: t.tasks.map(tk => tk.id === taskId ? { ...tk, status } : tk) } : t));
  }

  readonly stats = computed(() => {
    const all = this.tickets();
    const byStatus: Record<string, number> = {};
    for (const s of this.statuses()) byStatus[s.name] = 0;
    for (const t of all) byStatus[t.status] = (byStatus[t.status] ?? 0) + 1;
    const open = all.filter(t => !this.isTerminalStatus(t.status)).length;
    const closed = all.filter(t => this.isTerminalStatus(t.status)).length;
    const overdue = all.filter(t => this.isOverdue(t)).length;
    const unassigned = all.filter(t => !t.assignedTo).length;
    return { total: all.length, open, closed, overdue, unassigned, byStatus };
  });
}
